import React, { useState, useMemo } from 'react';
import { BookOpen, Copy, Check, Quote, ChevronDown, ChevronUp, AlignLeft } from 'lucide-react';
import DOMPurify from 'dompurify';

interface BookSynopsisCardProps {
  synopsis?: string | null;
  bookTitle?: string;
  className?: string;
  defaultExpanded?: boolean;
  maxCollapsedLength?: number;
  showCopy?: boolean;
}

export const BookSynopsisCard: React.FC<BookSynopsisCardProps> = ({
  synopsis,
  bookTitle,
  className = '',
  defaultExpanded = false,
  maxCollapsedLength = 260,
  showCopy = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [copied, setCopied] = useState(false);

  // Extract clean plain text for metrics, copying, and length evaluation
  const { isHtml, rawPlainText, sanitizedHtml } = useMemo(() => {
    if (!synopsis || !synopsis.trim()) {
      return { isHtml: false, rawPlainText: '', sanitizedHtml: '' };
    }

    const trimmed = synopsis.trim();
    const hasHtmlTags = /<[a-z][\s\S]*>/i.test(trimmed);

    if (hasHtmlTags) {
      const cleanHtml = DOMPurify.sanitize(trimmed, {
        ALLOWED_TAGS: ['p', 'b', 'i', 'u', 's', 'strong', 'em', 'strike', 'ul', 'ol', 'li', 'h3', 'h4', 'blockquote', 'br', 'span'],
        ALLOWED_ATTR: ['class'],
      });

      // Extract text content cleanly from sanitized html
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = cleanHtml;
      const text = tempDiv.textContent || tempDiv.innerText || '';

      return {
        isHtml: true,
        rawPlainText: text.trim(),
        sanitizedHtml: cleanHtml,
      };
    }

    return {
      isHtml: false,
      rawPlainText: trimmed,
      sanitizedHtml: '',
    };
  }, [synopsis]);

  if (!rawPlainText) {
    return (
      <div className={`p-4 rounded-2xl bg-slate-50/80 border border-dashed border-slate-200 text-center ${className}`}>
        <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mx-auto mb-1.5">
          <BookOpen className="w-4 h-4" />
        </div>
        <p className="text-xs font-semibold text-slate-600">Belum Ada Sinopsis</p>
        <p className="text-[11px] text-slate-400 mt-0.5">
          Penulis atau penerbit belum melampirkan ikhtisar/sinopsis resmi untuk naskah ini.
        </p>
      </div>
    );
  }

  const wordCount = rawPlainText.split(/\s+/).filter(Boolean).length;
  const readMinutes = Math.max(1, Math.ceil(wordCount / 180));
  const isLong = rawPlainText.length > maxCollapsedLength;

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(rawPlainText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Plain text fallback paragraphs
  const plainParagraphs = rawPlainText.split(/\r?\n\r?\n/).filter(Boolean);

  return (
    <div
      className={`relative overflow-hidden rounded-2xl bg-gradient-to-br from-white via-slate-50/60 to-indigo-50/30 border border-slate-200/90 shadow-2xs transition-all duration-200 ${className}`}
    >
      {/* Decorative subtle background watermark quote */}
      <div className="absolute right-2 -top-1 pointer-events-none opacity-[0.06] text-indigo-900 select-none">
        <Quote className="w-20 h-20 rotate-180" />
      </div>

      {/* Header bar */}
      <div className="px-4 py-3 border-b border-slate-100/90 flex items-center justify-between gap-3 bg-white/70 backdrop-blur-xs">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-lg bg-indigo-50 border border-indigo-200/60 text-indigo-700 flex items-center justify-center shrink-0 shadow-2xs">
            <AlignLeft className="w-3.5 h-3.5" />
          </div>
          <div className="min-w-0">
            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider truncate">
              Sinopsis & Ikhtisar Karya
            </h4>
          </div>
        </div>

        {/* Action pills */}
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-200/60">
            {wordCount} kata • ~{readMinutes} mnt baca
          </span>
          
          {showCopy && (
            <button
              type="button"
              onClick={handleCopy}
              title="Salin Teks Sinopsis"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white hover:bg-slate-100 active:bg-slate-200 text-slate-600 hover:text-slate-900 border border-slate-200 text-[11px] font-medium transition cursor-pointer shadow-2xs"
            >
              {copied ? (
                <>
                  <Check className="w-3 h-3 text-emerald-600 shrink-0" />
                  <span className="text-emerald-700 font-semibold text-[10px]">Tersalin</span>
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3 text-slate-400 shrink-0" />
                  <span className="text-[10px]">Salin</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Content body */}
      <div className="p-4 sm:p-4.5">
        <div
          className={`border-l-2 border-indigo-400/80 pl-3.5 sm:pl-4 transition-all duration-200 ${
            !isExpanded && isLong ? 'max-h-36 overflow-hidden relative' : ''
          }`}
        >
          {isHtml ? (
            <div
              className="synopsis-dropcap prose prose-sm sm:prose-base max-w-none text-slate-700 leading-relaxed sm:leading-7 2xl:leading-8 font-normal text-justify text-sm sm:text-base lg:text-[16px] 2xl:text-[17.5px]
                [&>p]:mb-3 [&>p:last-child]:mb-0
                [&>ul]:list-disc [&>ul]:pl-5 [&>ul]:mb-3 [&>ul]:space-y-1.5
                [&>ol]:list-decimal [&>ol]:pl-5 [&>ol]:mb-3 [&>ol]:space-y-1.5
                [&>blockquote]:border-l-2 [&>blockquote]:border-indigo-300 [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:text-slate-600 [&>blockquote]:my-3
                [&>h3]:text-base [&>h3]:sm:text-lg [&>h3]:font-bold [&>h3]:text-slate-900 [&>h3]:mt-3 [&>h3]:mb-1.5
                [&_strong]:font-bold [&_strong]:text-slate-900"
              dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
            />
          ) : (
            <div className="space-y-3">
              {plainParagraphs.map((p, idx) => (
                <p
                  key={`synopsis-p-${idx}`}
                  className={`text-sm sm:text-base lg:text-[16px] 2xl:text-[17.5px] text-slate-700 leading-relaxed sm:leading-7 2xl:leading-8 font-normal tracking-normal text-justify ${
                    idx === 0 ? 'synopsis-dropcap-first' : ''
                  }`}
                >
                  {p}
                </p>
              ))}
            </div>
          )}

          {/* Fade gradient when collapsed */}
          {!isExpanded && isLong && (
            <div className="absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none" />
          )}
        </div>

        {/* Read More / Read Less Toggle */}
        {isLong && (
          <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setIsExpanded(!isExpanded)}
              className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition cursor-pointer group"
            >
              <span>{isExpanded ? 'Tampilkan Lebih Sedikit' : 'Baca Sinopsis Selengkapnya'}</span>
              {isExpanded ? (
                <ChevronUp className="w-3.5 h-3.5 transition-transform group-hover:-translate-y-0.5" />
              ) : (
                <ChevronDown className="w-3.5 h-3.5 transition-transform group-hover:translate-y-0.5" />
              )}
            </button>
            <span className="text-[10px] text-slate-400 sm:hidden">
              {wordCount} kata
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

